import { CheckBox, FormNotice, Spinner } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { ChangeEvent, useState, useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { StyledH2 } from '../../../pages/UploadDocuments/UploadDocuments.styles';
import { USERS_TYPES } from '../../../shared/constants';
import { IGuarantor } from '../../../shared/ts/guarantor';
import { IGuest } from '../../../shared/ts/user';
import useStore from '../../../store';
import DropDownInput from '../../___design/DropDownInput';
import TextInput from '../../___design/TextInput';
import {
  StyledButton,
  StyledDeleteIcon,
} from '../../DocumentField/DocumentField.styles';
import {
  ButtonContainer,
  RowContainer,
  StyledContinueButton,
} from '../AddATenant/AddATenant.styles';
import UploadGuarantorDocuments from '../UploadGuarantorDocuments';
import { checkForDuplicateEmails } from '../UploadTenantDocuments/utils/requiredFieldForDocumentTenant';
import { StyledButtonWrapper } from './AddAGuarantor.styles';
import { filterRequiredFieldForGurantor } from './utils/requiredFieldForGurantor';

const AddAGuarantor = ({
  index,
  guarantor,
  handleMissingFormRequiredFieldGurantor,
  handleGuarantorDelete,
  deleteMisingFormRequiredFieldGurantor,
  shouldDeleteGurantorForOccupation,
}: {
  index: number;
  guarantor: IGuarantor;
  handleMissingFormRequiredFieldGurantor: (
    index: number,
    missingFields: {
      label: string;
      fields: string[];
      documents: {
        label: string;
        fields: { label: string; sideLabel?: string }[];
      };
    },
  ) => void;
  handleGuarantorDelete: (index: number) => void;
  deleteMisingFormRequiredFieldGurantor: (index: number) => void;
  shouldDeleteGurantorForOccupation: boolean;
}): JSX.Element => {
  const { t } = useTranslation(['form', 'documentsflow', 'routes']);
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking_id');
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);

  const {
    guarantors,
    guests,
    postGuarantor,
    updateGuarantor,
    deleteGuarantor,
    deleteGuestById,
    createGuestByBookingId,
  } = useStore(
    state => ({
      guarantors: state.guarantors,
      guests: state.guests,
      postGuarantor: state.postGuarantor,
      updateGuarantor: state.updateGuarantor,
      deleteGuarantor: state.deleteGuarantor,
      deleteGuestById: state.deleteGuestById,
      createGuestByBookingId: state.createGuestByBookingId,
    }),
    shallow,
  );

  const [formData, setFormData] = useState({
    firstName: guarantor.first_name ?? '',
    lastName: guarantor.last_name ?? '',
    email: guarantor.email ?? '',
    placeOfBirth: guarantor.place_of_birth ?? '',
    monthlyIncome: guarantor?.income?.toString() ?? '',
    dateOfBirth: guarantor.date_of_birth ?? '',
    tax_code: guarantor.tax_code ?? '',
    occupation: guarantor.occupation ?? '',
  });

  const [guarantorUserId, setGuarantorUserId] = useState(
    guarantor.user_id ? Number(guarantor.user_id) : null,
  );
  const [guarantorId, setGuarantorId] = useState(guarantor.id ?? null);
  const [isGuarantorGuest, setIsGuarantorGuest] = useState<boolean | null>(
    null,
  );

  const [no_tax_code_declaration, setTaxCodeDeclaration] = useState<boolean>(
    guarantor.no_tax_code_declaration === 1 ? true : false,
  );
  const [duplicateEmailError, setDuplicateEmailError] = useState(false);

  useEffect(() => {
    const matchingGuest = guests.find(
      (guest: IGuest) => Number(guest.user_id) === Number(guarantor.user_id),
    );
    if (matchingGuest) {
      setIsGuarantorGuest(true);
    }
  }, [guests, guarantor.user_id]);

  const [guarantorMissingDocuments, setGuarantortMissingDocuments] = useState<
    { label: string; fields: { label: string; sideLabel?: string }[] }[]
  >([]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = event.target;
      if (name === 'email') {
        const error = checkForDuplicateEmails(
          formData.email,
          index,
          'guarantor',
          guests,
          guarantors,
        );
        setDuplicateEmailError(error);
      }
      setFormData(prevData => ({ ...prevData, [name]: value }));
    },
    [],
  );

  const handleOnAddGuarantor = () => {
    if (areInputsFilled) {
      mutateAsync();
    }
  };

  const { mutateAsync, isLoading } = useMutation(async () => {
    const {
      firstName,
      lastName,
      occupation,
      email,
      tax_code,
      monthlyIncome,
      placeOfBirth,
      dateOfBirth,
    } = formData;

    let guarantorId = guarantor.id;
    let userId = Number(guarantor.user_id) ?? null;
    setGuarantorUserId(userId);
    if (!guarantorId) {
      if (isGuarantorGuest) {
        const user = await createGuestByBookingId(bookingId!, {
          name: { first_name: firstName, last_name: lastName },
          occupation: occupation,
          contacts: { email: email },
          place_of_birth: placeOfBirth,
          date_of_birth: dateOfBirth,
          tax_code: tax_code,
          monthly_income: parseFloat(monthlyIncome),
        });
        userId = Number(user.user_id);
        setGuarantorUserId(userId ?? null);
      }
      guarantorId = await postGuarantor(
        bookingId!,
        firstName,
        lastName,
        occupation,
        false,
        email,
        tax_code,
        parseFloat(monthlyIncome),
        placeOfBirth,
        dateOfBirth,
        userId ?? null,
        no_tax_code_declaration ?? false,
      );
    } else {
      const updatedGuarantor = await updateGuarantor(
        guarantor.id,
        bookingId!,
        firstName,
        lastName,
        occupation,
        isGuarantorGuest,
        email,
        tax_code,
        parseFloat(monthlyIncome),
        placeOfBirth,
        dateOfBirth,
        isGuarantorGuest ? null : guarantorUserId,
        no_tax_code_declaration ?? false,
      );
      guarantorId = updatedGuarantor.id;
      userId = Number(updatedGuarantor.user_id);
      setGuarantorUserId(Number(updatedGuarantor.user_id) ?? null);
    }
    setGuarantorId(guarantorId);
  });
  const areInputsFilled = Object.entries(formData).every(([key, value]) => {
    if (key === 'email') {
      return (
        value !== '' &&
        value !== null &&
        value !== undefined &&
        !duplicateEmailError &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      );
    }
    if (key === 'tax_code') {
      return true;
    }
    return value !== '' && value !== null && value !== undefined;
  });

  const { control } = useForm();
  const isFieldEmpty = (field: keyof typeof formData) => {
    if (field === 'tax_code') {
      return false;
    }
    return !formData[field];
  };
  const emailError =
    !formData.email.includes('@') ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  function getFieldType(field: string) {
    switch (field) {
      case 'dateOfBirth':
        return 'date';
      case 'email':
        return 'email';
      case 'monthlyIncome':
        return 'number';
      default:
        return 'text';
    }
  }

  const handleMissingGuarantorDocument = (
    index: number,
    missingFields: {
      label: string;
      fields: { label: string; sideLabel?: string }[];
    },
  ) => {
    setGuarantortMissingDocuments(prevFields => {
      const updatedFields = [...prevFields];
      updatedFields[index] = missingFields;
      return updatedFields;
    });
  };

  useEffect(() => {
    const missingValue = filterRequiredFieldForGurantor({
      index,
      guarantor: formData,
    });
    handleMissingFormRequiredFieldGurantor(index, {
      ...missingValue,
      documents:
        guarantorMissingDocuments[guarantorMissingDocuments?.length - 1],
    });
  }, [formData, index, guarantorMissingDocuments]);

  const handleOnDeleteButtonClick = async () => {
    if (guarantorId) {
      await deleteGuarantor(guarantorId);
      if (isGuarantorGuest && guarantorUserId) {
        setIsDeleteClicked(true);
        await deleteGuestById(bookingId!, guarantorUserId!);
        setIsDeleteClicked(false);
      }
    } else {
      handleGuarantorDelete(index);
    }
    deleteMisingFormRequiredFieldGurantor(index);
  };
  return (
    <>
      <RowContainer>
        <StyledH2>{upperFirst(t('documentsflow:addAGuarantor'))}</StyledH2>
        <StyledButtonWrapper>
          {index === 0 && shouldDeleteGurantorForOccupation ? (
            <></>
          ) : (
            <>
              {isDeleteClicked && <Spinner />}
              <StyledButton
                onClick={handleOnDeleteButtonClick}
                title={t('a11y:deleteTenant')}
              >
                <StyledDeleteIcon />
              </StyledButton>
            </>
          )}
        </StyledButtonWrapper>
      </RowContainer>
      <CheckBox
        id={`is-guarantor-guest-${guarantor.id || Math.random()}`}
        name="is-guarantor-guest"
        label={t('documentsflow:isGuarantorGuest')}
        checked={isGuarantorGuest ?? false}
        onChange={(event: { target: { checked: unknown } }) => {
          setIsGuarantorGuest(event.target.checked ? true : false);
          setTaxCodeDeclaration(false);
        }}
      />
      <br />
      <DropDownInput
        required={true}
        control={control}
        id="occupation"
        name="occupation"
        label={upperFirst(t('form:occupation'))}
        onSelect={handleInputChange}
        value={formData.occupation}
        options={[
          {
            label: upperFirst(t('form:pensioner')),
            value: USERS_TYPES.PENSIONER,
          },
          {
            label: upperFirst(t('form:selfEmployed')),
            value: USERS_TYPES.SELF_EMPLOYED,
          },
          {
            label: upperFirst(t('form:employee')),
            value: USERS_TYPES.EMPLOYEE,
          },
        ]}
      />
      {[
        'monthlyIncome',
        'firstName',
        'lastName',
        'email',
        'placeOfBirth',
        'dateOfBirth',
        'tax_code',
      ].map(field => (
        <Controller
          key={field}
          control={control}
          name={field}
          render={({ field: {} }) => (
            <TextInput
              key={field}
              id={field}
              label={upperFirst(t(`form:${field}`))}
              name={field}
              type={getFieldType(field)}
              value={formData[field as keyof typeof formData] as string}
              onChange={handleInputChange}
              required={field !== 'tax_code' ? true : false}
            >
              {isFieldEmpty(field as keyof typeof formData) && (
                <FormNotice isWrapped={true}>
                  {t('form:requiredField')}
                </FormNotice>
              )}
              {field === 'email' &&
                emailError &&
                !isFieldEmpty(field as keyof typeof formData) && (
                  <FormNotice isWrapped={true}>
                    {t('form:emailIsNotValid')}
                  </FormNotice>
                )}
              {field === 'email' &&
                duplicateEmailError &&
                !isFieldEmpty(field as keyof typeof formData) && (
                  <FormNotice isWrapped={true}>
                    {t('form:duplicateEmail')}
                  </FormNotice>
                )}
            </TextInput>
          )}
        />
      ))}
      {!isGuarantorGuest && (
        <CheckBox
          id={`noFiscalCodeDeclaration-${guarantor.id || Math.random()}`}
          name="noFiscalCodeDeclaration"
          label={t('documentsflow:noFiscalCodeDeclaration')}
          checked={no_tax_code_declaration ?? false}
          onChange={(event: { target: { checked: unknown } }) =>
            setTaxCodeDeclaration(event.target.checked ? true : false)
          }
        />
      )}
      <ButtonContainer>
        {isLoading ? (
          <Spinner />
        ) : (
          <StyledContinueButton
            disabled={!areInputsFilled}
            onClick={handleOnAddGuarantor}
            title={t('common:confirm')}
          >
            {t('common:confirm')}
          </StyledContinueButton>
        )}
      </ButtonContainer>

      {guarantorId !== null &&
        (formData.occupation || guarantor.occupation) && (
          <UploadGuarantorDocuments
            index={index}
            guarantor={guarantor}
            occupation={formData.occupation ?? guarantor.occupation}
            handleMissingGuarantorDocument={handleMissingGuarantorDocument}
          />
        )}
    </>
  );
};

export default AddAGuarantor;
